import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  InputAdornment,
} from "@mui/material";
import { DraggablePaper } from "./DraggablePaper";
import { useDebouncedCallback } from "../utils/useDebouncedCallback";

// Memoize AngleDialog to prevent unnecessary re-renders
const AngleDialog = React.memo(({ open, onClose, canvas, selectedObject }) => {
  const [angle, setAngle] = useState(0);
  const [prevAngle, setPrevAngle] = useState(0);
  const [isDirty, setIsDirty] = useState(false);
  const [inputAngle, setInputAngle] = useState("");
  const selectedObjectRef = useRef(selectedObject);

  // Update ref when selectedObject changes
  useEffect(() => {
    selectedObjectRef.current = selectedObject;
    console.log("[AngleDialog] selectedObject updated:", selectedObject);
  }, [selectedObject]);

  // Initialize angle value when dialog opens or selected object changes
  useEffect(() => {
    if (selectedObject && open && selectedObject.type !== "circle") {
      const currentAngle = Math.round(Number(selectedObject.get("angle")) || 0);
      setAngle(currentAngle);
      setInputAngle(currentAngle.toString());
      setPrevAngle(currentAngle);
      setIsDirty(false);
      console.log("[AngleDialog] Initialized angle:", currentAngle);
    }
  }, [selectedObject, open]);

  // Memoize update callback
  const updateAngleCallback = useCallback(
    (value) => {
      const roundedValue = Number(value);
      setAngle(roundedValue);
      setInputAngle(roundedValue.toString());
      if (selectedObjectRef.current && canvas) {
        canvas.setActiveObject(selectedObjectRef.current);
        selectedObjectRef.current.set({ angle: roundedValue });
        selectedObjectRef.current.setCoords();
        canvas.requestRenderAll();
        setIsDirty(true);
        console.log(
          "[AngleDialog] Updated angle:",
          roundedValue,
          "Object:",
          selectedObjectRef.current
        );
      } else {
        console.warn("[AngleDialog] Update failed:", {
          selectedObject: selectedObjectRef.current,
          canvas,
        });
      }
    },
    [canvas]
  );

  // Create debounced update function
  const updateAngle = useDebouncedCallback(updateAngleCallback, 100, canvas);

  // Handle dialog close (cancel)
  const handleCancel = useCallback(() => {
    if (isDirty && selectedObject) {
      selectedObject.set({ angle: prevAngle });
      selectedObject.setCoords();
      canvas?.requestRenderAll();
      console.log("[AngleDialog] Canceled, reverted to angle:", prevAngle);
    }
    onClose();
  }, [isDirty, selectedObject, prevAngle, canvas, onClose]);

  // Handle apply angle (save changes and add to history)
  const handleApply = useCallback(() => {
    updateAngle.flush();
    if (isDirty && canvas && selectedObject) {
      canvas.setActiveObject(selectedObject);
      selectedObject.set({ angle });
      selectedObject.setCoords();
      canvas.requestRenderAll();
      canvas.fire("object:modified", { target: selectedObject });
      console.log(
        "[AngleDialog] Applied angle:",
        angle,
        "Fired object:modified for:",
        selectedObject
      );
    } else {
      console.log(
        "[AngleDialog] Apply skipped: isDirty=",
        isDirty,
        "canvas=",
        !!canvas,
        "selectedObject=",
        !!selectedObject
      );
    }
    setIsDirty(false);
    onClose();
  }, [isDirty, canvas, selectedObject, angle, updateAngle, onClose]);

  // Stop mouse events from propagating to allow input interaction
  const stopPropagation = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent backdrop click from closing the dialog
  const handleBackdropClick = useCallback((e) => {
    e.stopPropagation();
  }, []);

  // Prevent keydown events (e.g., Escape key) from closing the dialog
  const handleKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
    }
  }, []);

  // Handle input changes
  const handleAngleChange = useCallback((e) => {
    setInputAngle(e.target.value);
  }, []);

  const handleAngleBlur = useCallback(() => {
    const value = Number(inputAngle);
    if (!isNaN(value) && value >= 0) {
      updateAngle(value);
    } else {
      setInputAngle(angle.toString());
    }
  }, [inputAngle, angle, updateAngle]);

  const handleAngleStep = useCallback(
    (e) => {
      const value = Number(e.target.value);
      if (!isNaN(value) && value >= 0) {
        updateAngle(value);
      }
    },
    [updateAngle]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleCancel();
      }}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.2)" },
        onClick: handleBackdropClick,
      }}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "#fff",
          boxShadow: "0px 3px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle
        id="draggable-dialog-title"
        sx={{
          color: "#ffffff",
          backgroundColor: "#0d2445",
          fontSize: "20px",
          display: "flex",
          alignItems: "center",
          cursor: "move",
        }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ marginRight: "8px" }}
        >
          <circle cx="4" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="4" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="8" r="1.5" fill="#ffffff" />
          <circle cx="4" cy="12" r="1.5" fill="#ffffff" />
          <circle cx="8" cy="12" r="1.5" fill="#ffffff" />
        </svg>
        Angle Toolbar
      </DialogTitle>
      <DialogContent
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Stack direction="column" sx={{ marginTop: 1 }}>
          <TextField
            type="number"
            id="canvasAngle"
            margin="dense"
            value={inputAngle}
            onChange={handleAngleChange}
            onBlur={handleAngleBlur}
            onInput={handleAngleStep}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              endAdornment: <InputAdornment position="end">deg</InputAdornment>,
              sx: {
                bgcolor: "#fff",
                height: 36,
                width: 122,
                paddingTop: 1,
                marginRight: 1,
              },
              inputProps: { step: 1, min: 0 }, // Step 1deg for fine control
            }}
            label="Angle"
            sx={{
              "& .MuiInputLabel-root": {
                fontFamily: "Roboto",
                fontWeight: 500,
                marginTop: 1,
              },
            }}
            variant="outlined"
            inputProps={{ "aria-label": "Angle in degrees" }}
          />
        </Stack>
      </DialogContent>
      <DialogActions
        onMouseDown={stopPropagation}
        onMouseMove={stopPropagation}
        onMouseUp={stopPropagation}
        onMouseLeave={stopPropagation}
      >
        <Button
          onClick={handleCancel}
          color="secondary"
          aria-label="Cancel angle changes"
        >
          Cancel
        </Button>
        <Button
          onClick={handleApply}
          color="primary"
          aria-label="Apply angle changes"
        >
          Apply Angle
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default AngleDialog;
